import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Megaphone, Upload, Copy, Check, Trash2, Play, Pause, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SOURCES = ["telegram", "x", "instagram", "meta", "tiktok", "partner"];
const STATUS_STYLE = {
  active: "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10",
  paused: "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10",
  pending: "text-[#007AFF] border-[#007AFF]/40 bg-[#007AFF]/10",
  rejected: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10",
};

export default function AdsManagerPanel() {
  const [ads, setAds] = useState(null);
  const [form, setForm] = useState({ title: "", copy_text: "", landing_path: "/launch", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const fileRef = useRef(null);

  const aiCopy = async () => {
    if (!form.title) { toast.error("Enter the advert title first — AI writes copy from it"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/ad-copy", { title: form.title, notes: form.copy_text });
      setSuggestions(data.variations);
      toast.success("3 copy variations generated — click one to use it");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI copywriter failed — try again");
    } finally { setAiBusy(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/ads/manage");
      setAds(data);
    } catch { setAds([]); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Creative uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.title || !form.copy_text || !form.image_url) { toast.error("Title, copy and a creative are required"); return; }
    setSaving(true);
    try {
      await api.post("/ads", form);
      setForm({ title: "", copy_text: "", landing_path: "/launch", image_url: "" });
      toast.success("Advert is live in the Ads API");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create advert");
    } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/ads/${id}`, { status });
      toast.success(`Advert ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update advert");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this advert and its stats permanently?")) return;
    try {
      await api.delete(`/ads/${id}`);
      toast.success("Advert deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete advert");
    }
  };

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="ads-manager-panel">
      <div className="flex items-center gap-2">
        <Megaphone className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-black text-xl tracking-tight">Ads API — Marketing engine</h2>
        {ads && <span className="text-xs text-neutral-500 font-mono">({ads.length})</span>}
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Host adverts, hand out tracked links per platform, embed on partner sites. Feed:{" "}
        <span className="font-mono text-neutral-400">{API_BASE}/ads/feed</span>
      </p>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Advert title" data-testid="ad-title-input"
            className="w-full h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#FF3B30]" />
          <textarea value={form.copy_text} onChange={(e) => setForm({ ...form, copy_text: e.target.value })}
            placeholder="Ad copy — what traders will read" rows={3} maxLength={600} data-testid="ad-copy-input"
            className="w-full rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF3B30]" />
          <button onClick={aiCopy} disabled={aiBusy} data-testid="ad-ai-copy-btn"
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-full border border-[#7C3AED]/50 text-xs font-bold text-[#a78bfa] hover:border-[#7C3AED] hover:text-white transition-colors disabled:opacity-50">
            <Sparkles className="h-3.5 w-3.5" /> {aiBusy ? "Writing…" : "AI copy — 3 variations"}
          </button>
          {suggestions.length > 0 && (
            <div className="space-y-1.5" data-testid="ad-ai-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => { setForm((f) => ({ ...f, copy_text: s })); setSuggestions([]); toast.success("Copy applied"); }}
                  data-testid={`ad-ai-suggestion-${i}`}
                  className="block w-full text-left text-xs text-neutral-300 rounded-md border border-[#27272A] bg-[#0A0A0A] px-3 py-2 hover:border-[#7C3AED] transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}
          <input value={form.landing_path} onChange={(e) => setForm({ ...form, landing_path: e.target.value })}
            placeholder="Landing path (e.g. /launch)" data-testid="ad-landing-input"
            className="w-full h-10 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white font-mono focus:outline-none focus:border-[#FF3B30]" />
        </div>
        <div className="flex flex-col gap-3">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="ad-upload-btn"
            className="h-10 inline-flex items-center justify-center gap-2 rounded-md border border-dashed border-[#3f3f46] text-sm text-neutral-300 hover:border-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : form.image_url ? "Replace creative" : "Upload creative (image)"}
          </button>
          {form.image_url && (
            <img src={`${API_BASE.replace(/\/api$/, "")}${form.image_url}`} alt="creative preview" className="h-24 w-auto rounded-md border border-[#27272A] object-cover self-start" data-testid="ad-creative-preview" />
          )}
          <button onClick={create} disabled={saving} data-testid="ad-create-btn"
            className="h-10 rounded-full bg-[#FF3B30] text-white font-bold text-sm hover:bg-[#e0342a] transition-colors disabled:opacity-50">
            {saving ? "Publishing…" : "Publish advert"}
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid="ads-list">
        {ads === null ? (
          <div className="text-neutral-500 text-sm">Loading…</div>
        ) : ads.length === 0 ? (
          <div className="text-neutral-500 text-sm">No adverts yet — publish your first one above.</div>
        ) : ads.map((a) => <AdRow key={a.id} ad={a} onStatus={setStatus} onDelete={remove} />)}
      </div>
    </div>
  );
}

function AdRow({ ad, onStatus, onDelete }) {
  const [copied, setCopied] = useState("");
  const origin = API_BASE.replace(/\/api$/, "");
  const copy = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        toast.error("Could not copy — select and copy manually: " + text);
        return;
      }
    }
    setCopied(label);
    toast.success(`${label} link copied`);
    setTimeout(() => setCopied(""), 1500);
  };
  const embedCode = `<iframe src="${origin}/api/ads/embed/${ad.code}" width="360" height="420" frameborder="0" title="VISMARKS ad"></iframe>`;

  return (
    <div className="rounded-lg border border-[#27272A] bg-[#0A0A0A] p-4" data-testid={`ad-row-${ad.code}`}>
      <div className="flex flex-wrap items-start gap-4">
        <img src={`${origin}${ad.image_url}`} alt={ad.title} className="h-16 w-16 rounded-md object-cover border border-[#27272A]" />
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-bold">{ad.title}</span>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[ad.status]}`} data-testid={`ad-status-${ad.code}`}>{ad.status}</span>
            {ad.creator_role === "seller" && <span className="text-[10px] uppercase tracking-widest text-neutral-500">seller submission</span>}
          </div>
          <div className="text-xs text-neutral-400 mt-1 line-clamp-2">{ad.copy_text}</div>
          <div className="text-[11px] font-mono text-neutral-500 mt-1">→ {ad.landing_path} · code {ad.code}</div>
        </div>
        <div className="flex gap-5 text-center">
          <Metric label="Impr." value={ad.impressions} />
          <Metric label="Clicks" value={ad.clicks} />
          <Metric label="CTR" value={`${ad.ctr_pct}%`} />
          <Metric label="Signups" value={ad.signups} accent />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {SOURCES.map((s) => (
          <button key={s} onClick={() => copy(s, `${origin}/api/ads/r/${ad.code}?src=${s}`)} data-testid={`ad-copy-link-${ad.code}-${s}`}
            className="h-7 px-2.5 rounded-full border border-[#27272A] text-[11px] uppercase tracking-wider text-neutral-400 hover:border-[#007AFF] hover:text-white transition-colors inline-flex items-center gap-1">
            {copied === s ? <Check className="h-3 w-3 text-[#34C759]" /> : <Copy className="h-3 w-3" />} {s}
          </button>
        ))}
        <button onClick={() => copy("embed", embedCode)} data-testid={`ad-copy-embed-${ad.code}`}
          className="h-7 px-2.5 rounded-full border border-[#27272A] text-[11px] uppercase tracking-wider text-neutral-400 hover:border-[#FF3B30] hover:text-white transition-colors inline-flex items-center gap-1">
          {copied === "embed" ? <Check className="h-3 w-3 text-[#34C759]" /> : <Copy className="h-3 w-3" />} embed
        </button>
        <div className="flex-1" />
        {ad.status === "pending" && (
          <>
            <ActionBtn testid={`ad-approve-${ad.code}`} onClick={() => onStatus(ad.id, "active")} icon={Play} label="Approve" green />
            <ActionBtn testid={`ad-reject-${ad.code}`} onClick={() => onStatus(ad.id, "rejected")} icon={X} label="Reject" red />
          </>
        )}
        {ad.status === "active" && <ActionBtn testid={`ad-pause-${ad.code}`} onClick={() => onStatus(ad.id, "paused")} icon={Pause} label="Pause" />}
        {(ad.status === "paused" || ad.status === "rejected") && <ActionBtn testid={`ad-activate-${ad.code}`} onClick={() => onStatus(ad.id, "active")} icon={Play} label="Activate" green />}
        <ActionBtn testid={`ad-delete-${ad.code}`} onClick={() => onDelete(ad.id)} icon={Trash2} label="Delete" red />
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className={`font-mono font-bold text-sm ${accent ? "text-[#34C759]" : "text-white"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, green, red, testid }) {
  const color = green ? "hover:border-[#34C759] hover:text-[#34C759]" : red ? "hover:border-[#FF3B30] hover:text-[#FF3B30]" : "hover:border-white hover:text-white";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-7 px-2.5 rounded-full border border-[#27272A] text-[11px] uppercase tracking-wider text-neutral-400 transition-colors inline-flex items-center gap-1 ${color}`}>
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}
